import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Settings from "@/pages/Settings";
import CarrierOnboarding from "@/pages/CarrierOnboarding";
import PayLink from "@/pages/PayLink";
import EmployeeOnboarding from "@/pages/EmployeeOnboarding";
import ActiveCarriers from "@/pages/ActiveCarriers";
import DriverInfo from "@/pages/DriverInfo";
import HR from "@/pages/HR";
import Dashboard from "@/pages/Dashboard";
import DriverPortal from "@/pages/DriverPortal";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import SubmitLead from "@/pages/SubmitLead";
import RejectedLeads from "@/pages/RejectedLeads";
import MyActivity from "@/pages/MyActivity";
import Leaderboard from "@/pages/Leaderboard";
import Loads from "@/pages/Loads";
import OperatingCosts from "@/pages/OperatingCosts";
import TrailerRenting from "@/pages/TrailerRenting";
import AsterismAnalytics from "@/pages/AsterismAnalytics";
import AsterismAI from "@/pages/AsterismAI";
import FreightNews from "@/pages/FreightNews";
import LoadMap from "@/pages/LoadMap";
import Dispatch from "@/pages/Dispatch";
import Fleet from "@/pages/Fleet";
import Drivers from "@/pages/Drivers";
import Safety from "@/pages/Safety";
import Tasks from "@/pages/Tasks";
import Invoices from "@/pages/Invoices";
import CarrierAuthorization from "@/pages/CarrierAuthorization";
import CarrierSetup from "@/pages/CarrierSetup";
import CarrierPortal from "@/pages/CarrierPortal";
import OffboardCarriers from "@/pages/OffboardCarriers";
import Receivable from "@/pages/Receivable";
import Payable from "@/pages/Payable";
import CarrierBrokers from "@/pages/CarrierBrokers";
import RoutePlanning from "@/pages/RoutePlanning";
import Team from "@/pages/Team";
import Announcements from "@/pages/Announcements";
import Groups from "@/pages/Groups";
import Notes from "@/pages/Notes";
import Brokers from "@/pages/Brokers";
import Alerts from "@/pages/Alerts";
import Activity from "@/pages/Activity";
import BreakLog from "@/pages/BreakLog";
import CarrierSnaps from "@/pages/CarrierSnaps";
import CarrierTracking from "@/pages/CarrierTracking";
import DDCL from "@/pages/DDCL";
import CpmCalculator from "@/pages/CpmCalculator";
import Marketing from "@/pages/Marketing";
import Earnings from "@/pages/Earnings";
import TrackShare from "@/pages/TrackShare";
import Weather from "@/pages/Weather";

function Home() {
  const { user } = useAuth();
  if (user?.role === "driver") return <Navigate to="/my-loads" replace />;
  if (user?.role === "carrier") return <Navigate to="/carrier-portal" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/carrier-setup" element={<CarrierSetup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/carrier-onboarding/:token" element={<CarrierOnboarding />} />
            <Route path="/pay/:token" element={<PayLink />} />
            <Route path="/track/:token" element={<TrackShare />} />
            <Route path="/t/:token" element={<TrackShare />} />
            <Route path="/onboarding/:token" element={<EmployeeOnboarding />} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Home />} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
              <Route path="/carrier-portal" element={<ProtectedRoute roles={["carrier"]}><CarrierPortal /></ProtectedRoute>} />
              <Route path="/my-loads" element={<ProtectedRoute roles={["driver"]}><DriverPortal /></ProtectedRoute>} />
              <Route path="/leads" element={<ProtectedRoute roles={["admin", "sales"]}><Leads /></ProtectedRoute>} />
              <Route path="/leads/:id" element={<ProtectedRoute roles={["admin", "sales"]}><LeadDetail /></ProtectedRoute>} />
              <Route path="/submit-lead" element={<ProtectedRoute roles={["admin", "sales"]}><SubmitLead /></ProtectedRoute>} />
              <Route path="/rejected-leads" element={<ProtectedRoute roles={["admin", "sales"]}><RejectedLeads /></ProtectedRoute>} />
              <Route path="/my-activity" element={<ProtectedRoute roles={["admin", "sales", "dispatcher"]}><MyActivity /></ProtectedRoute>} />
              <Route path="/leaderboard" element={<ProtectedRoute roles={["admin", "sales"]}><Leaderboard /></ProtectedRoute>} />
              <Route path="/loads" element={<ProtectedRoute roles={["admin", "dispatcher"]}><Loads /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute roles={["admin", "sales", "dispatcher"]}><AsterismAnalytics /></ProtectedRoute>} />
              <Route path="/trailer-renting" element={<ProtectedRoute roles={["admin"]}><TrailerRenting /></ProtectedRoute>} />
              <Route path="/operating-costs" element={<ProtectedRoute roles={["admin"]}><OperatingCosts /></ProtectedRoute>} />
              <Route path="/asterism-ai" element={<ProtectedRoute roles={["admin", "dispatcher"]}><AsterismAI /></ProtectedRoute>} />
              <Route path="/freight-news" element={<ProtectedRoute roles={["admin", "dispatcher", "sales", "driver"]}><FreightNews /></ProtectedRoute>} />
              <Route path="/dispatch" element={<ProtectedRoute roles={["admin", "dispatcher"]}><Dispatch /></ProtectedRoute>} />
              <Route path="/fleet" element={<ProtectedRoute roles={["admin", "dispatcher"]}><Fleet /></ProtectedRoute>} />
              <Route path="/drivers" element={<ProtectedRoute roles={["admin", "dispatcher"]}><Drivers /></ProtectedRoute>} />
              <Route path="/driver-info" element={<ProtectedRoute roles={["admin", "dispatcher"]}><DriverInfo /></ProtectedRoute>} />
              <Route path="/carriers" element={<ProtectedRoute roles={["admin"]}><ActiveCarriers /></ProtectedRoute>} />
              <Route path="/carrier-tracking" element={<ProtectedRoute roles={["admin", "sales", "dispatcher"]}><CarrierTracking /></ProtectedRoute>} />
              <Route path="/ddcl" element={<ProtectedRoute roles={["admin", "dispatcher"]}><DDCL /></ProtectedRoute>} />
              <Route path="/cpm-calculator" element={<ProtectedRoute roles={["admin", "sales", "dispatcher"]}><CpmCalculator /></ProtectedRoute>} />
              <Route path="/marketing" element={<ProtectedRoute roles={["admin", "sales", "dispatcher"]}><Marketing /></ProtectedRoute>} />
              <Route path="/telecalling" element={<Navigate to="/marketing" replace />} />
              <Route path="/earnings" element={<ProtectedRoute roles={["admin", "dispatcher"]}><Earnings /></ProtectedRoute>} />
              <Route path="/carrier-snaps" element={<ProtectedRoute roles={["admin", "sales", "dispatcher"]}><CarrierSnaps /></ProtectedRoute>} />
              <Route path="/break-tracker" element={<ProtectedRoute roles={["admin"]}><BreakLog /></ProtectedRoute>} />
              <Route path="/hr" element={<ProtectedRoute roles={["admin"]}><HR /></ProtectedRoute>} />
              <Route path="/safety" element={<ProtectedRoute roles={["admin", "dispatcher"]}><Safety /></ProtectedRoute>} />
              <Route path="/fleet-map" element={<ProtectedRoute roles={["admin", "dispatcher"]}><LoadMap /></ProtectedRoute>} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/invoices" element={<ProtectedRoute roles={["admin"]}><Invoices /></ProtectedRoute>} />
              <Route path="/offboard-carriers" element={<ProtectedRoute roles={["admin"]}><OffboardCarriers /></ProtectedRoute>} />
              <Route path="/carrier-authorization" element={<ProtectedRoute roles={["admin"]}><CarrierAuthorization /></ProtectedRoute>} />
              <Route path="/receivable" element={<ProtectedRoute roles={["admin", "dispatcher"]}><Receivable /></ProtectedRoute>} />
              <Route path="/payable" element={<ProtectedRoute roles={["admin"]}><Payable /></ProtectedRoute>} />
              <Route path="/carrier-brokers" element={<ProtectedRoute roles={["admin", "dispatcher"]}><CarrierBrokers /></ProtectedRoute>} />
              <Route path="/route-planning" element={<ProtectedRoute roles={["admin", "dispatcher"]}><RoutePlanning /></ProtectedRoute>} />
              <Route path="/team" element={<ProtectedRoute roles={["admin"]}><Team /></ProtectedRoute>} />
              <Route path="/announcements" element={<Announcements />} />
              <Route path="/groups" element={<Groups />} />
              <Route path="/notes" element={<Notes />} />
              <Route path="/brokers" element={<ProtectedRoute roles={["admin", "dispatcher"]}><Brokers /></ProtectedRoute>} />
              <Route path="/alerts" element={<Alerts />} />
              <Route path="/activity" element={<Activity />} />
              <Route path="/weather" element={<Weather />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </ThemeProvider>
        <Toaster position="top-right" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
